import { useCallback } from 'react'
import type { ExistingCustomManufacturer } from '../CustomManufacturerFieldsWrapper'

// Bug 2706261: persists typed values across virtualization unmount/remount.
const customManufacturerCache = new Map<string, string>()

export const clearCustomManufacturerCache = (): void =>
    customManufacturerCache.clear()

interface UseCustomManufacturerCacheArgs {
    elementId: string
    parentElementId?: string
}

interface UseCustomManufacturerCacheReturn {
    cacheKeysFor: (index: number, pillId?: string) => string[]
    readSlotValue: (
        index: number,
        serverPill: ExistingCustomManufacturer | undefined
    ) => string
    cache: Map<string, string>
}

// Encoded so a `|` inside an id can't collide segments.
const keySegment = (key: string): string => encodeURIComponent(key)

export const useCustomManufacturerCache = ({
    elementId,
    parentElementId,
}: UseCustomManufacturerCacheArgs): UseCustomManufacturerCacheReturn => {
    const rowKey = `${keySegment(elementId)}|${keySegment(
        parentElementId ?? ''
    )}`

    const cacheKeysFor = useCallback(
        (index: number, pillId?: string): string[] => {
            const keys: string[] = []
            if (pillId) {
                keys.push(`${rowKey}|${keySegment(pillId)}`)
            }
            keys.push(`${rowKey}|slot-${index}`)
            return keys
        },
        [rowKey]
    )

    const readSlotValue = useCallback(
        (
            index: number,
            serverPill: ExistingCustomManufacturer | undefined
        ): string => {
            for (const key of cacheKeysFor(index, serverPill?.id)) {
                const cached = customManufacturerCache.get(key)
                if (cached !== undefined) return cached
            }
            return serverPill?.name ?? ''
        },
        [cacheKeysFor]
    )

    return { cacheKeysFor, readSlotValue, cache: customManufacturerCache }
}
